import React, { useContext } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation,
  PAGE_ROUTE_ABOUT_PRODUCT,
  PAGE_ROUTE_ADD_PRODUCT_ITEM_SOP,
  PAGE_ROUTE_SOPS
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import { ADD_PRODUCT_SOP_SECTIONS } from "./constants/addProductSopContent";
import { ADD_PRODUCT_SOP_IMAGES } from "./constants/addProductSopImages";
import type { AddProductSopImageConfig } from "./types";
import {
  publicInfoBodySx,
  publicInfoGuideSectionSx,
  publicInfoLinkSx,
  publicInfoOrderedListSx,
  publicInfoSectionTitleWithImageSx,
  userGuideSectionImageSx
} from "./constants/publicInfoPageStyles";

const AddProductSopImage: React.FC<{
  image: AddProductSopImageConfig;
  testId: string;
}> = ({ image, testId }) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box
      component="img"
      src={image.src}
      alt={t(image.altKey, image.altFallback)}
      data-testid={testId}
      sx={userGuideSectionImageSx}
    />
  );
};

const AddProductSopPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const aboutProductPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_ABOUT_PRODUCT, language, defaultLang)
      : `/${PAGE_ROUTE_ABOUT_PRODUCT}`;
  const sopsHubPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_SOPS, language, defaultLang)
      : `/${PAGE_ROUTE_SOPS}`;
  const addProductItemSopPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_ADD_PRODUCT_ITEM_SOP, language, defaultLang)
      : `/${PAGE_ROUTE_ADD_PRODUCT_ITEM_SOP}`;
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="add-product-sop-page"
        eyebrow={t("add-product-sop-page-eyebrow", "Run books")}
        title={t("add-product-sop-page-title", "How to add a new product")}
        intro={t(
          "add-product-sop-page-intro",
          "Set up a product correctly from the start by choosing its selling mode, category, base unit, and partial selling rules."
        )}
      />

      <PublicInfoCallout testId="add-product-sop-page-note">
        {t(
          "add-product-sop-page-note",
          "Required fields are marked with *. The fields you see may depend on your role. Company admins can select any store, while store-level users are usually limited to their assigned location."
        )}
      </PublicInfoCallout>

      <Stack spacing={4} sx={{ mt: 3 }}>
        {ADD_PRODUCT_SOP_SECTIONS.map((section, sectionIndex) => (
          <Box
            key={section.id}
            component="section"
            sx={publicInfoGuideSectionSx}
            data-testid={`add-product-sop-section-${section.id}`}
          >
            <Typography component="h2" sx={publicInfoSectionTitleWithImageSx}>
              {sectionIndex + 1}. {t(section.titleKey, section.titleFallback)}
            </Typography>
            <Box component="ol" sx={publicInfoOrderedListSx}>
              {section.steps.map((step, stepIndex) => {
                const image =
                  step.imageId != null
                    ? ADD_PRODUCT_SOP_IMAGES[step.imageId]
                    : undefined;

                return (
                  <Box key={`${section.id}-${stepIndex}`} component="li">
                    <Typography
                      component="span"
                      variant="body1"
                      sx={publicInfoBodySx}
                    >
                      {t(step.bodyKey, step.bodyFallback)}
                    </Typography>
                    {image ? (
                      <AddProductSopImage
                        image={image}
                        testId={`add-product-sop-image-${step.imageId}`}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <PublicInfoCtaPanel
        title={t("add-product-sop-page-cta-title", "Product saved. What next?")}
        testId="add-product-sop-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "add-product-sop-page-cta-body",
            "Add product items to record stock in each store, then print QR codes to make checkout faster and more accurate."
          )}{" "}
          <MuiLink
            component={Link}
            to={sopsHubPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("add-product-sop-page-cta-all-guides-link", "Run books")}
          </MuiLink>
          {t("add-product-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={addProductItemSopPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "add-product-sop-page-cta-add-product-item-link",
              "How to add a product item"
            )}
          </MuiLink>
          {t("add-product-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={aboutProductPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t(
              "add-product-sop-page-cta-about-product-link",
              "Products vs. product items"
            )}
          </MuiLink>
          {t("add-product-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("add-product-sop-page-cta-guide-link", "User guide")}
          </MuiLink>
          {t("add-product-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("add-product-sop-page-cta-contact-link", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default AddProductSopPage;
